import React from 'react';
import {
  WhiteInstantSearchBuilder,
  NoResults,
  Pagination,
  mappers,
  SearchBox,
  AutocompleteHighlight,
} from '@white-nl/instantsearch-components';
import { HitComponent } from '@white-nl/instantsearch-components/dist/types/components';
import { Hit, Loading } from '../components/search/results';
import Menu from '../components/search/refinements/Menu';
import AutocompleteItem from '../components/search/autocomplete/AutocompleteItem';
import AutocompleteSeeAll from '../components/search/autocomplete/AutocompleteSeeAll';

const isSearchPage = document.getElementById('search-page') || null;
const settings = document.getElementById('search-autocomplete')?.dataset || null;
if (settings) {
  const whiteInstantSearchSearch = new WhiteInstantSearchBuilder();
  const apiUrl = Craft.getSiteUrl('api/search');

  fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((res) => res.json())
    .then((data) => {
      const {
        translates,
      } = data;

      const { search, pagination } = translates;

      const sectionMapping = {
        page: Craft.t(search, 'Page'),
        case: Craft.t(search, 'Case'),
        insight: Craft.t(search, 'Insight'),
        job: Craft.t(search, 'Job'),
        plugin: Craft.t(search, 'Plugin'),
      };

      const indexName = settings.indexName || '';
      const initialQuery = new URLSearchParams(window.location.search).get('query') ?? undefined;

      whiteInstantSearchSearch
        .withSearchkit(
          indexName,
          settings.publicUrl || '',
          settings.jwt || '',
          settings.refreshUrl || '',
        )
        .addAdditionalData({ translates, sectionMapping })
        .addAutocompleteSearchingComponent([
          {
            settings: {
              placeholder: Craft.t(search, 'What are you looking for?'),
              useRecentSearches: false,
              classNames: {
                panel: 'absolute bg-white z-[999] xl:p-6 xl:pt-2 xl:rounded-b-3xl xl:!w-134.5',
                input: 'w-full py-3 pl-6 pr-21 text-near-black placeholder:text-near-black text-base leading-snug rounded-full outline-0 transition-all border border-grey-200 xl:border-transparent bg-white xl:bg-transparent h-[3.0625rem] xl:h-[2.9375rem]',
                submitButton: 'flex items-center justify-center w-10 absolute inset-y-0 right-2 rounded-3xl cursor-pointer transition-colors text-near-black/40 hover:text-near-black focus-visible:outline-none focus-visible:text-near-black z-30 pointer-events-auto',
                clearButton: 'group flex items-center justify-center w-10 absolute inset-y-0 right-11.5 rounded-3xl cursor-pointer focus-visible:outline-none after:absolute after:right-0 after:content-[\'\'] after:w-px after:bg-grey-200 after:h-[calc(100%-16px)] *:bg-near-black/40 *:group-hover:bg-near-black *:group-focus:bg-near-black *:transition-colors *:fill-white *:p-1.5 *:w-6 *:rounded-full z-30',
                loadingIndicator: 'flex items-center justify-center w-10 absolute inset-y-0 right-2 rounded-3xl cursor-pointer transition-colors text-near-black/40 hover:text-near-black focus-visible:outline-none focus-visible:text-near-black z-30',
              },
              ...(initialQuery ? { initialState: { query: initialQuery } } : {}),
              formPartOrder: ['input', 'suffix', 'prefix'],
              translations: {
                clearButtonTitle: Craft.t(search, 'Clear'),
                submitButtonTitle: Craft.t(search, 'Submit'),
              },
              disableHoverActiveItemNavigationOnEnter: true,
              sources: [
                {
                  getQueryParams: (query) => ({
                    query,
                    filters: `siteId:${Craft.siteId}`,
                    hitsPerPage: 3,
                  }),
                  transformResponse: ({ hits }: { hits: any[] }) => {
                    const mapped = hits[0].map((hit: any) => ({
                      ...hit,
                      url: Craft.getSiteUrl(hit.uri),
                      breadcrumbs: Craft.t(search, 'in {section}', { section: sectionMapping[hit.section as keyof typeof sectionMapping] }),
                    }));
                    if (mapped.length >= 3 && !isSearchPage) {
                      mapped.push({
                        isSeeAll: true,
                        title: Craft.t(search, 'View all results'),
                        url: settings.baseUrl,
                      });
                    }
                    return mapped;
                  },
                  templates: {
                    item: ({
                      item,
                      components,
                      source,
                      state,
                    }) => {
                      if (item.isSeeAll) {
                        return (
                          <AutocompleteSeeAll title={item.title} url={source.getItemUrl!({ item, state })!} />
                        );
                      }

                      return (
                        <AutocompleteItem
                          hit={item}
                          url={source.getItemUrl!({ item, state })!}
                          components={{ ...components, Highlight: AutocompleteHighlight }}
                          titleAttribute="title"
                          descriptionAttribute="breadcrumbs"
                          translations={{
                            fillQueryButtonText: Craft.t(search, 'Fill query with'),
                            selectButtonText: Craft.t(search, 'Select'),
                          }}
                          onFillQuery={() => source.onFillQuery({ item, state })}
                        />
                      );
                    },
                  },
                  getItemInputValue: ({ item, state }) => (
                    item.isSeeAll ? state.query : item.title
                  ),
                  getItemUrl: ({ item, state }) => (
                    item.isSeeAll
                      ? `${settings.baseUrl}?query=${encodeURIComponent(state.query ?? '')}`
                      : Craft.getSiteUrl(item.uri)
                  ),
                },
              ],
              onSubmit: ({ state, event }: any) => {
                // Check if an explicit DOM/UI event was passed with the form submission
                if (event) {
                  // Prevent the event from triggering parent listeners (e.g., nested Alpine.js components or parent forms)
                  if (typeof event.stopPropagation === 'function') {
                    event.stopPropagation();
                  }
                  // Prevent standard browser form submission and page reloads
                  if (typeof event.preventDefault === 'function') {
                    event.preventDefault();
                  }
                }

                // Fallback: Manually query the DOM input element if state doesn't capture the value
                const inputEl = document.querySelector<HTMLInputElement>('#white-instant-search-search-box-autocomplete input');

                // Prefer state-managed query string, but fall back to the direct DOM element's current value
                const query = state?.query || inputEl?.value;

                // Redirect the user to the dedicated search results page if they aren't already on it and a query exists
                if (!isSearchPage && query) {
                  window.location.href = `${settings.baseUrl}?query=${encodeURIComponent(query)}`;
                }
              },
            },
          },
        ], '#white-instant-search-search-box-autocomplete');
      if (isSearchPage) {
        whiteInstantSearchSearch
          .withWhiteInstantSearchRouting({
            baseUrl: settings.baseUrl,
            addToPath: true,
            addTrailingSlashesToUrls: Craft.trailingSlashes === true,
            writeDelay: 0,
          })
          .addSearchingComponent([
            {
              settings: {
                placeholder: Craft.t(search, 'What are you looking for?'),
                classNames: {
                  form: 'flex relative w-full lg:w-1/2 mt-12',
                  input: 'bg-white rounded-full border border-grey-200 focus-visible:border-near-black focus-visible:outline-none w-full py-3.5 px-6 placeholder:text-gray-500 text-near-black text-base',
                  submit: 'flex items-center justify-center w-12 absolute inset-y-0 right-2.5 rounded-3xl cursor-pointer transition-colors text-near-black/40 hover:text-near-black focus-visible:outline-none focus-visible:text-near-black',
                  submitIcon: 'w-4.75 fill-current',
                  reset: 'group flex items-center justify-center w-12 absolute inset-y-0 right-14.5 rounded-3xl cursor-pointer focus-visible:outline-none after:absolute after:right-0 after:content-[\'\'] after:w-px after:bg-grey-200 after:h-[calc(100%-16px)]',
                  resetIcon: 'bg-near-black/40 group-hover:bg-near-black group-focus:bg-near-black transition-colors fill-white p-1.5 w-6 rounded-full',
                  loadingIndicator: 'hidden',
                },
              },
              component: SearchBox,
            },
          ])
          .addMenu([
            {
              settings: {
                attribute: 'section',
                classNames: {
                  list: 'flex flex-wrap gap-3',
                  item: 'relative',
                  selectedItem: 'btn-dark text-white focus-visible:shadow-white',
                  link: 'btn btn-outline-dark btn-sm leading-none items-start',
                  label: 'flex gap-x-4',
                  count: 'font-light text-xs lg:text-sm',
                },
                sortBy: (item1, item2) => {
                  // custom order sorting
                  const value1 = item1.escapedValue.toLowerCase();
                  const value2 = item2.escapedValue.toLowerCase();
                  const keys = Object.keys(sectionMapping);

                  if (keys.includes(value1) && keys.includes(value2)) {
                    if (keys.indexOf(value1) > keys.indexOf(value2)) {
                      return 1;
                    }
                    if (keys.indexOf(value1) < keys.indexOf(value2)) {
                      return -1;
                    }
                    return 0;
                  }

                  if (keys.includes(value1) && !keys.includes(value2)) {
                    return -1;
                  }
                  if (!keys.includes(value1) && keys.includes(value2)) {
                    return 1;
                  }

                  return 0;
                },
                transformItems: (items) => {
                  const itemsWithData = items.map((item) => ({ ...item, data: null }));
                  return mappers.transformers
                    .mapLabels(sectionMapping)(itemsWithData);
                },
                hardRefresh: false,
              },
              component: Menu,
            },
          ])
          .addHitComponent([
            {
              settings: {
                loading: {
                  translations: {
                    loading: Craft.t(search, 'Loading...'),
                  },
                },
                noResults: {
                  translations: {
                    noResults: Craft.t(search, 'Sorry, we couldn\'t find any results!'),
                    refinements: Craft.t(search, 'Try to choose another filter.'),
                    query: Craft.t(search, 'Please try another query.'),
                  },
                },
              },
              component: Hit as HitComponent,
              loadingComponent: Loading,
              noResultsComponent: NoResults,
            },
          ])
          .setScrolling({
            includeAttributes: ['page'],
          })
          .addPaginationComponent([
            {
              settings: {
                showFirst: false,
                showLast: false,
                classNames: {
                  noRefinementRoot: 'hidden',
                  list: 'list-none flex flex-wrap justify-center items-center text-lg leading-tight gap-3 md:gap-4 font-medium text-near-black',
                  pageItem: 'group/link *:justify-center *:w-10.5 *:aspect-[1/1] *:bg-white *:border *:border-2 *:border-white *:group-[.active]/link:bg-near-black *:bg-white',
                  link: 'rounded-sm inline-flex items-center no-underline group-[.active]/link:text-white group-[.active]/link:pointer-events-none hover:border-near-black',
                  previousPageItem: 'max-md:basis-full flex justify-center md:justify-start grow basis-0 text-center md:text-left text-base lg:text-lg h-10.5',
                  selectedItem: 'active',
                  disabledItem: 'text-near-black/40 pointer-events-none',
                  nextPageItem: 'max-md:basis-full flex justify-center md:justify-end max-md:items-end grow basis-0 md:text-right text-base lg:text-lg h-10.5',
                },
                translations: {
                  previousPageItemText: Craft.t(pagination, 'Previous'),
                  nextPageItemText: Craft.t(pagination, 'Next'),
                },
              },
              component: Pagination,
            },
          ])
          .setHitsPerPage(10);
      } else {
        whiteInstantSearchSearch
          .withoutRouting();
      }
      whiteInstantSearchSearch
        .afterFirstRender(() => {
          const searchBox = document.getElementById('white-instant-search-search-box-autocomplete');

          searchBox?.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target.closest('button[type="submit"]') || target.closest('.aa-SubmitButton')) {
              e.stopPropagation();
            }
          }, true);

          document.getElementById('open-search')?.addEventListener('click', () => {
            setTimeout(() => {
              document.getElementById('autocomplete-0-input')?.focus();
            }, 300);
          });
        })
        .where(`siteId:${Craft.siteId}`)
        .build()
        .show();
    });
}
